import React, { useEffect, useState } from "react";
import PopupModules from "src/features/auth/components/PopupModules";
import InputBox from "../../components/InputBox";
import { Button } from "src/business-types/grocery/components/Button";
import { notifyError } from "src/assets/toastService";
import { Order } from "src/shared/services/interface";
interface Props {
  payment: boolean;
  cancel: () => void;
  totalSOB: number;
  order: Order;
  callApi: (data: any) => void;
  payOnly: boolean;
  orderStatus: string;
}
function PaymentPopup({ payment, cancel, totalSOB = 0, order, callApi, payOnly, orderStatus }: Props) {
  const [selectedOption, setSelectedOption] = useState<string>("Cash");
  const [resPrice, setResPrice] = useState<string>("");

  const now = new Date();
  const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");

  useEffect(() => {
    setResPrice("");
  }, []);

  // 🛡️ Prevent crash if order is not loaded
  if (!order) {
    return (
      <PopupModules isOpen={payment} onClose={cancel} title='Order Payment'>
        <div className='p-4 text-center text-gray-500'>Loading order details...</div>
      </PopupModules>
    );
  }

  const finalAmount = Number(order?.Finalamount ?? 0);
  const alreadyPaid = (order.paymentHistory ?? []).reduce((acc, item) => acc + Number(item?.ReceivedAmount || 0), 0);
  const adj = (order.paymentHistory ?? []).reduce((acc, item) => acc + Number(item?.adjustAmount || 0), 0);

  const remaining = totalSOB + finalAmount - alreadyPaid - adj;
  const newBalance = remaining - Number(resPrice || 0);
  const OrderStatus = order?.OrderStatus

  const radioBtn = (title: string) => (
    <div
      key={title}
      className={`px-2 py-1 flex gap-2 items-center cursor-pointer border rounded-md ${selectedOption === title ? "bg-blue-100 border-blue-500" : "border-gray-300"
        }`}
      onClick={() => setSelectedOption(title)}
    >
      <input type='radio' name='payment' className='w-5 h-5' checked={selectedOption === title} onChange={() => setSelectedOption(title)} />
      <p className='capitalize'>{title}</p>
    </div>
  );

  const pricePayment = (pricePayment: string) => {
    // Allow numbers with up to 2 decimal places
    const decimalRegex = /^\d+(\.\d{0,2})?$/;

    if (!decimalRegex.test(pricePayment)) {
      if (pricePayment !== "") {
        console.warn("Only up to 2 decimal places are allowed.");
      } else {
        setResPrice("");
      }
      return;
    }

    const number = Number(pricePayment);

    if (!isNaN(number) && number >= 0 && number <= remaining) {
      setResPrice(pricePayment);
    } else {
      console.warn("Invalid input or exceeds remaining amount.");
    }
  };

  const paymentProcess = () => {
    const amount = Number(resPrice);
    if (totalSOB <= 0) {
      notifyError("Outstanding balance already cleared.");
      setResPrice("");
      return;
    }

    const updatedRemaining = remaining - amount;
    const basePayment = {
      RemainingAmount: updatedRemaining,
      outstanding_balance: Number(totalSOB),
      id: order.Customerid,
      Orderdate: order.Orderdate,
      Ordertime: order.Ordertime,
      OrderStatus: OrderStatus,
      status_change: OrderStatus === orderStatus ? false : true,
      payments: [
        {
          Paymentmethod: selectedOption,
          ReceivedAmount: amount,
        },
      ],
      paymentHistory: [
        ...(order.paymentHistory || []),
        {
          Paymentmethod: selectedOption,
          ReceivedAmount: amount,
          balance: updatedRemaining,
          date: localDate,
        },
      ],
    };

    const fullPayment = {
      ...basePayment,
      DeliveryDate: localDate,
      DeliveryTime: now.toTimeString().split(" ")[0],
    };

    setResPrice("");
    callApi(payOnly ? basePayment : fullPayment);
  };

  const orderReturnAmount =
    order?.returnHistory?.reduce((acc, p) => {
      const itemTotal = p.items?.reduce((sum, i) => {
        return (
          sum +
          Number(i.netAmount)
        );
      }, 0);

      return acc + itemTotal;
    }, 0) ?? 0;

  return (
    <PopupModules
      isOpen={payment}
      onClose={() => {
        cancel();
        setResPrice("");
      }}
      title='Order Payment'
    >
      <div className='pb-2'>
        <div className='bg-gray-100 rounded-lg p-4 mb-4'>
          <h2 className='text-lg font-medium text-gray-700 mb-2'>Choose Payment Type</h2>
          <div className='flex gap-4 flex-wrap justify-center'>
            {radioBtn("Card")}
            {radioBtn("Cash")}
          </div>
        </div>

        <div className='bg-gray-100 rounded-lg p-4'>
          <h2 className='text-lg font-medium text-gray-700 mb-2'>Payment Summary</h2>
          <div className='grid grid-cols-2 gap-y-2 text-sm text-gray-700'>
            <p className='font-medium'>Total OSB:</p>
            <p>{Number(totalSOB).toFixed(2)}</p>

            <p className='font-medium'>Order Price:</p>
            <p>{finalAmount.toFixed(2)}</p>

            <p className='font-medium'>Return Order Price (-):</p>
            <p>{orderReturnAmount.toFixed(2)}</p>

            <p className='font-medium'>Already Paid:</p>
            <p>{alreadyPaid.toFixed(2)}</p>

            <p className='font-medium'>Remaining Balance:</p>
            <p>{remaining.toFixed(2)}</p>

            <p className='font-medium'>Enter Payment:</p>
            <div>
              <InputBox
                disabled={selectedOption === "Buy on credit"}
                min={0}
                type='text'
                onChange={(e) => pricePayment(e.target.value)}
                value={resPrice}
              />
            </div>

            <p className='font-medium'>New Balance:</p>
            <p>{newBalance.toFixed(2)}</p>
          </div>
        </div>

        <div className='flex justify-center gap-4 mt-6'>
          <Button
            label='Cancel'
            variant='cancel'
            onClick={() => {
              cancel();
              setResPrice("");
            }}
          />
          <Button label='Submit Payment' onClick={paymentProcess} disabled={selectedOption !== "Buy on credit" && Number(resPrice) === 0} />
        </div>
      </div>
    </PopupModules>
  );
}

export default PaymentPopup;
